import React from "react";
import "./styles/TermsAndConditions.css";
import { APP_NAME } from "../common/constants";
import SEO from "./SEO";

const TermsAndConditions: React.FC = () => {
  return (
    <>
      <SEO
        title={`Terms and Conditions – ${APP_NAME}`}
        description={`Review the full Terms and Conditions for using ${APP_NAME}, including acceptable use, liability limitations, user responsibilities, refund policy and governing law.`}
      />

      <div className="terms-container">
        <h1>Terms and Conditions</h1>

        <h2>1. Introduction</h2>
        <p>
          These Terms and Conditions (&quot;Terms&quot;) govern your access to and
          use of <strong>{APP_NAME}</strong> (the &quot;Service&quot;). By accessing
          or using the Service, you agree to be legally bound by these Terms. If
          you do not agree, you must not use the Service.
        </p>

        <h2>2. Eligibility & Acceptance</h2>
        <p>
          Your acceptance of these Terms occurs when you first access or use{" "}
          {APP_NAME}, including under the Free, Basic, Pro or Developer plans.
          If you purchase a subscription, acceptance also occurs upon successful
          payment. If you are using the Service on behalf of an organization, you
          represent that you have authority to bind that organization to these Terms.
        </p>

        <h2>3. Service Plans</h2>
        <p>The Service is provided under the following plans:</p>
        <ul>
          <li><strong>Free Plan</strong> – limited features and usage</li>
          <li><strong>Basic Plan</strong> – extended personal/commercial use</li>
          <li><strong>Pro Plan</strong> – advanced and multi-project professional use</li>
          <li><strong>Developer Plan</strong> – high-volume, integrations, automation and developer tools (where applicable)</li>
        </ul>
        <p>
          Specific features, usage limits, and pricing for each plan are defined
          on our Pricing page and may change from time to time. Your continued use
          of the Service after such changes constitutes acceptance of those updates.
        </p>

        <h2>4. User Content & Responsibility</h2>
        <p>
          You are solely responsible for all content you upload, submit,
          link, process or otherwise handle using the Service (&quot;User
          Content&quot;). By using the Service, you confirm that:
        </p>
        <ul>
          <li>
            You own or have obtained all necessary rights, consents, and licenses
            to use and process the User Content via {APP_NAME}.
          </li>
          <li>
            Your use of the Service does not infringe any copyright, trademark,
            intellectual property, privacy, contractual or other rights.
          </li>
          <li>
            Your use complies with applicable law and any relevant third-party
            platform terms.
          </li>
        </ul>
        <p>
          We do not control or monitor User Content and accept no responsibility
          for it. However, we reserve the right to remove or restrict access to
          content or accounts where we reasonably believe there is unlawful,
          infringing or abusive use.
        </p>

        <h2>5. Prohibited Use</h2>
        <p>You must not use the Service to:</p>
        <ul>
          <li>Upload or process content you do not have rights to</li>
          <li>Infringe copyright, intellectual property or privacy rights</li>
          <li>
            Circumvent technical restrictions, DRM, access controls or platform rules
          </li>
          <li>Engage in unlawful, harmful, fraudulent or abusive activity</li>
          <li>Resell or redistribute the Service as a standalone competing product</li>
          <li>Reverse engineer or modify the Service beyond what is permitted by law</li>
        </ul>

        <h2>6. Third-Party Platforms</h2>
        <p>
          The Service may interact with third-party platforms and hosting providers.
          {APP_NAME} is not affiliated with, endorsed by, or responsible for any
          third-party service or its terms. You are responsible for complying with
          any applicable third-party terms and legal requirements.
        </p>

        <h2>7. Refund Policy</h2>
        <p>
          Subscription fees and digital services are generally non-refundable unless
          required by applicable law. If you believe you are entitled to a refund,
          contact us at support@cutora.io for review.
        </p>

        <h2>8. Service Availability</h2>
        <p>
          We aim to provide reliable access but do not guarantee that the Service
          will always be available, uninterrupted, error-free, secure, or free of
          defects. We may modify, suspend, or discontinue the Service (or any part
          of it) at any time.
        </p>

        <h2>9. Disclaimer of Warranties</h2>
        <p>
          THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot;
          WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT
          NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE
          AND NON-INFRINGEMENT. YOU USE THE SERVICE AT YOUR OWN RISK.
        </p>

        <h2>10. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, {APP_NAME} AND ITS OWNERS,
          DIRECTORS, EMPLOYEES AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY OR PUNITIVE DAMAGES,
          INCLUDING LOSS OF PROFITS, DATA OR BUSINESS, ARISING OUT OF YOUR USE OF
          THE SERVICE.
        </p>
        <p>
          OUR TOTAL LIABILITY FOR ANY CLAIM SHALL NOT EXCEED THE AMOUNT YOU PAID
          (IF ANY) FOR ACCESS TO THE SERVICE IN THE 12 MONTHS PRIOR TO THE CLAIM.
        </p>

        <h2>11. Indemnity</h2>
        <p>
          You agree to indemnify and hold harmless {APP_NAME}, its owners, employees
          and affiliates from any claims, damages, losses, liabilities and expenses
          arising from your use of the Service, your User Content, or your breach of
          these Terms or applicable law.
        </p>

        <h2>12. Termination</h2>
        <p>
          We may suspend or terminate your access to the Service at any time if we
          reasonably believe you are violating these Terms, abusing the Service, or
          engaging in unlawful conduct. You may stop using the Service at any time.
        </p>

        <h2>13. Governing Law</h2>
        <p>
          These Terms are governed by the laws of the Republic of Bulgaria and
          applicable European Union law. Any disputes shall be resolved exclusively
          by the competent courts of Sofia, Bulgaria.
        </p>

        <h2>14. Changes to Terms</h2>
        <p>
          We may update these Terms from time to time. When updated, the effective
          date below will change. Continued use of the Service after updates
          constitutes acceptance of the revised Terms.
        </p>

        <h2>15. Contact</h2>
        <p>
          For questions, legal queries, or complaints, contact us at:<br />
          <a href="mailto:support@cutora.io">support@cutora.io</a>
        </p>

        <p className="terms-updated">Last updated: 21 December 2025</p>
      </div>
    </>
  );
};

export default TermsAndConditions;
